/// <reference types="@webgpu/types" />
//neuralEngine.ts — WebGPU neural 2× upscale, the learned counterpart to ui/upscaler.ts.
//Runs the Anime4K CNN-x2-S graph (4 conv layers, 4 channels, CReLU) over the live video
//texture and adds its residual to a bilinear 2×, so a 720×1280 feed lands on a 1440×2560
//canvas. Unlike the shader path this one invents detail, so it is a mode, not the default.
//Ported from @websr/websr (MIT); see weights/README.md for why it is a port, not a dep.

import { createNetworkGraph, OUTPUT_SCALE, type NeuralNetworkGraph } from './neuralNetwork'
import type { SourceSize } from './networkShaders'

export interface UpscaleEngine {
	readonly label: string
	render(video: HTMLVideoElement): void
	dispose(): void
}

const LABEL = 'Neural 2× (Anime4K CNN-S, Real Life)'
//The Lucy feed's resolution. Building for it up front means the first frame renders instead
//of being dropped while pipelines compile; any other size rebuilds on the frame it appears.
const EXPECTED_SOURCE: SourceSize = { width: 720, height: 1280 }
//Deep queues turn into input lag on a mirror, so stop submitting once the GPU is behind.
const MAX_FRAMES_IN_FLIGHT = 2

export async function createNeuralEngine(canvas: HTMLCanvasElement): Promise<UpscaleEngine> {
	if (!navigator.gpu) throw new Error('Neural upscale unavailable: this browser has no WebGPU')
	//Probe now: a canvas that already handed out a 2d/webgl context can never give a WebGPU
	//one, and finding that out on the first frame is too late for the caller to fall back.
	const context = canvas.getContext('webgpu')
	if (!context) throw new Error('Neural upscale unavailable: canvas cannot provide a webgpu context')

	const device = await requestDevice()
	try {
		const format = navigator.gpu.getPreferredCanvasFormat()
		context.configure({ device, format, alphaMode: 'opaque' })
		const graph = await createNetworkGraph(device, EXPECTED_SOURCE, format)
		return new NeuralEngine(canvas, context, device, format, graph, EXPECTED_SOURCE)
	} catch (error) {
		device.destroy()
		throw new Error(`Neural upscale init failed: ${describe(error)}`)
	}
}

//Health hooks for callers and the bench. Not part of UpscaleEngine, so they stay optional.
const stateByEngine = new WeakMap<UpscaleEngine, NeuralEngine>()

export function engineDevice(engine: UpscaleEngine): GPUDevice | undefined {
	return stateByEngine.get(engine)?.gpuDevice
}

//True once the engine has gone permanently inert — a lost device, or a graph that would not
//rebuild. The caller can watch this and swap back to the shader upscaler.
export function engineFailed(engine: UpscaleEngine): boolean {
	return stateByEngine.get(engine)?.isFailed ?? false
}

class NeuralEngine implements UpscaleEngine {
	readonly label = LABEL
	private graph: NeuralNetworkGraph | null
	private size: SourceSize
	private framesInFlight = 0
	private rebuilding = false
	private disposed = false
	private failed = false

	constructor(
		private readonly canvas: HTMLCanvasElement,
		private readonly context: GPUCanvasContext,
		private readonly device: GPUDevice,
		private readonly format: GPUTextureFormat,
		graph: NeuralNetworkGraph,
		size: SourceSize,
	) {
		this.graph = graph
		this.size = size
		this.resizeCanvas(size)
		stateByEngine.set(this, this)
		//A lost device cannot be revived: go inert instead of throwing every frame.
		void device.lost.then((info) => {
			if (info.reason === 'destroyed') return
			this.failed = true
			console.error('Neural upscale device lost', info.message)
		})
	}

	get gpuDevice(): GPUDevice {
		return this.device
	}

	get isFailed(): boolean {
		return this.failed
	}

	render(video: HTMLVideoElement): void {
		if (this.disposed || this.failed || this.rebuilding) return

		const width = video.videoWidth
		const height = video.videoHeight
		if (!width || !height || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return

		if (width !== this.size.width || height !== this.size.height) {
			//The resolution is baked into the WGSL and the buffer sizes, so a new source shape
			//means a new graph. Frames are dropped until it is ready.
			void this.rebuild({ width, height })
			return
		}

		const graph = this.graph
		if (!graph || this.framesInFlight >= MAX_FRAMES_IN_FLIGHT) return
		this.resizeCanvas(this.size)

		try {
			const source = this.device.importExternalTexture({ source: video })
			const encoder = this.device.createCommandEncoder({ label: 'neural-upscale' })
			graph.encode(encoder, source, this.context.getCurrentTexture().createView())
			this.device.queue.submit([encoder.finish()])
		} catch (error) {
			this.failed = true
			console.error('Neural upscale render failed', error)
			return
		}

		this.framesInFlight += 1
		void this.device.queue.onSubmittedWorkDone().then(() => {
			this.framesInFlight -= 1
		})
	}

	dispose(): void {
		if (this.disposed) return
		this.disposed = true
		stateByEngine.delete(this)
		this.graph?.destroy()
		this.graph = null
		this.context.unconfigure()
		this.device.destroy()
	}

	private async rebuild(size: SourceSize): Promise<void> {
		this.rebuilding = true
		try {
			const graph = await createNetworkGraph(this.device, size, this.format)
			if (this.disposed) {
				graph.destroy()
				return
			}
			this.graph?.destroy()
			this.graph = graph
			this.size = size
			this.resizeCanvas(size)
		} catch (error) {
			this.failed = true
			console.error('Neural upscale rebuild failed', error)
		} finally {
			this.rebuilding = false
		}
	}

	//Assigning canvas.width clears the drawing buffer, so only touch it when it is wrong.
	private resizeCanvas({ width, height }: SourceSize): void {
		const outputWidth = width * OUTPUT_SCALE
		const outputHeight = height * OUTPUT_SCALE
		if (this.canvas.width === outputWidth && this.canvas.height === outputHeight) return
		this.canvas.width = outputWidth
		this.canvas.height = outputHeight
	}
}

async function requestDevice(): Promise<GPUDevice> {
	const gpu = navigator.gpu
	if (!gpu) throw new Error('Neural upscale unavailable: this browser has no WebGPU')
	const adapter = await gpu.requestAdapter()
	if (!adapter) throw new Error('Neural upscale unavailable: no WebGPU adapter')
	try {
		return await adapter.requestDevice()
	} catch (error) {
		throw new Error(`Neural upscale unavailable: device request failed (${describe(error)})`)
	}
}

function describe(error: unknown): string {
	return error instanceof Error ? error.message : String(error)
}
